/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   capacityMetricsApi.ts
 *     Description     :   API service for capacity metrics operations
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-01-19 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.11.0 (Specification Testing Standards Edition)
 *     Feature         :   030-capacity-metrics (T066)
 * =========================================================================================
 */

// T024: Use centralized apiClient for auth interceptor and error handling
import { apiClient } from './apiClient';
import type {
  CapacityDashboardSummary,
  CapacityMetricHourly,
  CapacityMetricDaily,
  LatestHourThrottling,
  TrendDirection,
  WorkspaceAttributionRow,
} from '../../../shared/types/capacityMetrics';

// API base path for capacity metrics endpoints
const API_PATH = '/api/capacity-metrics';

// ============================================================================
// Types
// ============================================================================

export interface DashboardResponse {
  capacities: CapacityDashboardSummary[];
  total: number;
  timestamp: string;
}

export interface DashboardFilters {
  capacity_type?: 'Reference' | 'ClientDedicated' | 'MultiTenant' | 'Platform';
  region?: string;
  alert_status?: 'ok' | 'warning' | 'critical';
  sort_by?: 'capacity_name' | 'cu_used_percent' | 'capacity_type' | 'region';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface HourlyMetricsQuery {
  capacity_id?: string;
  capacity_type?: 'Reference' | 'ClientDedicated' | 'MultiTenant' | 'Platform';
  start_time?: string;
  end_time?: string;
  limit?: number;
  offset?: number;
}

export interface HourlyMetricsResponse {
  data: CapacityMetricHourly[];
  total: number;
  limit: number;
  offset: number;
}

export interface DailyMetricsQuery {
  capacity_id?: string;
  capacity_type?: 'Reference' | 'ClientDedicated' | 'MultiTenant' | 'Platform';
  days?: number;
  start_date?: string;
  end_date?: string;
}

export interface DailyMetricsResponse {
  data: CapacityMetricDaily[];
  total: number;
  days: number;
}

export interface CapacityDetailResponse {
  capacity_id: string;
  capacity_name: string;
  capacity_type: string;
  region?: string;
  /** Null for a SKU Microsoft does not size, or when no hour is recorded (Spec 286 T160 item 5). */
  cu_allocation: number | null;
  /** The latest telemetry hour's CU, else the latest recorded hour's; null when nothing measured it. */
  current_utilization: number | null;
  trend: TrendDirection;
  hourly_history: CapacityMetricHourly[];
  daily_history: CapacityMetricDaily[];
  workspace_attribution: WorkspaceAttributionRow[];
  threshold_config?: {
    warning_percent: number;
    critical_percent: number;
    alerts_enabled: boolean;
  };
  // Spec 157 — FR-004: capacity-level storage rollup
  total_storage_gb?: number;
  /** Spec 286 T159 item 32: how many items total_storage_gb counts at their last known size (not measured). */
  sizeNotMeasuredCount?: number;
  /** Throttling in the capacity's latest telemetry hour; null when it has no telemetry. */
  latest_hour_throttling?: LatestHourThrottling | null;
  /** Spec 297 T032: the capacity's flag and retirement stamp (ISO); the page says "retired on" from them. */
  is_active?: boolean;
  deactivated_at?: string | null;
}

export interface CapacityAlert {
  id: string;
  capacity_id: string;
  capacity_name: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  alert_type: string;
  threshold_breached: number;
  current_value: number;
  triggered_at: Date;
  acknowledged_at?: Date;
  acknowledged_by?: string;
}

export interface ActiveAlertsResponse {
  alerts: CapacityAlert[];
  total: number;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get dashboard data with sparklines and trends (T053)
 * Performance target: <2 seconds
 */
export async function getDashboard(filters?: DashboardFilters): Promise<DashboardResponse> {
  const params = new URLSearchParams();
  
  if (filters?.capacity_type) params.append('capacity_type', filters.capacity_type);
  if (filters?.region) params.append('region', filters.region);
  if (filters?.alert_status) params.append('alert_status', filters.alert_status);
  if (filters?.sort_by) params.append('sort_by', filters.sort_by);
  if (filters?.sort_order) params.append('sort_order', filters.sort_order);
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.offset) params.append('offset', String(filters.offset));

  const queryString = params.toString();
  const url = `${API_PATH}/dashboard${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiClient.get<{ success: boolean; data: DashboardResponse }>(url);
  return response.data.data;
}

/**
 * Get hourly metrics with time range filtering (T054)
 */
export async function getHourlyMetrics(query?: HourlyMetricsQuery): Promise<HourlyMetricsResponse> {
  const params = new URLSearchParams();
  
  if (query?.capacity_id) params.append('capacity_id', query.capacity_id);
  if (query?.capacity_type) params.append('capacity_type', query.capacity_type);
  if (query?.start_time) params.append('start_time', query.start_time);
  if (query?.end_time) params.append('end_time', query.end_time);
  if (query?.limit) params.append('limit', String(query.limit));
  if (query?.offset) params.append('offset', String(query.offset));

  const queryString = params.toString();
  const url = `${API_PATH}/hourly${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiClient.get<{ success: boolean; data: HourlyMetricsResponse }>(url);
  return response.data.data;
}

/**
 * Get daily aggregated metrics (T055)
 */
export async function getDailyMetrics(query?: DailyMetricsQuery): Promise<DailyMetricsResponse> {
  const params = new URLSearchParams();
  
  if (query?.capacity_id) params.append('capacity_id', query.capacity_id);
  if (query?.capacity_type) params.append('capacity_type', query.capacity_type);
  if (query?.days) params.append('days', String(query.days));
  if (query?.start_date) params.append('start_date', query.start_date);
  if (query?.end_date) params.append('end_date', query.end_date);

  const queryString = params.toString();
  const url = `${API_PATH}/daily${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiClient.get<{ success: boolean; data: DailyMetricsResponse }>(url);
  return response.data.data;
}

/**
 * Get detailed capacity info with history and attribution (T056)
 */
export async function getCapacityDetail(
  capacityId: string,
  historyHours: number = 24
): Promise<CapacityDetailResponse> {
  const params = new URLSearchParams();
  if (historyHours !== 24) params.append('historyHours', String(historyHours));

  const queryString = params.toString();
  const url = `${API_PATH}/${capacityId}${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiClient.get<{ success: boolean; data: CapacityDetailResponse }>(url);
  return response.data.data;
}

/**
 * Get active (unacknowledged) alerts
 */
export async function getActiveAlerts(
  capacityId?: string,
  severity?: string[]
): Promise<ActiveAlertsResponse> {
  const params = new URLSearchParams();
  
  if (capacityId) params.append('capacity_id', capacityId);
  if (severity?.length) {
    severity.forEach((s) => params.append('severity', s));
  }

  const queryString = params.toString();
  const url = `${API_PATH}/alerts/active${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiClient.get<{ success: boolean; data: ActiveAlertsResponse }>(url);
  return response.data.data;
}

/**
 * Acknowledge an alert for a capacity
 */
export async function acknowledgeAlert(
  capacityId: string,
  acknowledgedBy?: string
): Promise<void> {
  await apiClient.post(`${API_PATH}/alerts/${capacityId}/acknowledge`, {
    acknowledged_by: acknowledgedBy,
  });
}

/**
 * T072: Get workspace attribution for a capacity
 */
export async function getWorkspaceAttribution(
  capacityId: string,
  sortBy?: 'storage_gb' | 'last_activity_date' | 'storage_percent',
  sortOrder?: 'asc' | 'desc'
): Promise<{ capacity_id: string; workspaces: WorkspaceAttributionRow[]; total: number }> {
  const params = new URLSearchParams();
  
  if (sortBy) params.append('sort_by', sortBy);
  if (sortOrder) params.append('sort_order', sortOrder);

  const queryString = params.toString();
  const url = `${API_PATH}/${capacityId}/attribution${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiClient.get<{ 
    success: boolean; 
    data: { capacity_id: string; workspaces: WorkspaceAttributionRow[]; total: number }
  }>(url);
  return response.data.data;
}

/**
 * T073: Export workspace attribution as CSV
 */
export async function exportAttributionCsv(capacityId: string): Promise<string> {
  const response = await apiClient.get<string>(`${API_PATH}/${capacityId}/attribution/export`, {
    responseType: 'text',
    headers: {
      Accept: 'text/csv',
    },
  });
  return response.data;
}

// ============================================================================
// Export as module
// ============================================================================

const capacityMetricsApi = {
  getDashboard,
  getHourlyMetrics,
  getDailyMetrics,
  getCapacityDetail,
  getActiveAlerts,
  acknowledgeAlert,
  getWorkspaceAttribution,
  exportAttributionCsv,
};

export default capacityMetricsApi;

export { capacityMetricsApi };
